import { useEffect, useState } from 'react';
import { PageHeader, Panel, Toolbar, KeyValueList } from '../components/Page';
import { EmptyState, ErrorState, InlineError, CardSkeleton } from '../components/Feedback';
import { CheckboxField, SelectField, TextField, enumOptions } from '../components/Form';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Chip } from '../components/Chips';
import { RequireRole } from '../lib/auth';
import { payablesAgent } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { GatewayRole } from '../lib/enums';
import {
  VendorAgentStage,
  VendorAgentStageLabels,
  VendorAgentStageDescriptions,
  VendorAgentStageTones,
  ScheduledPaymentStatus,
  ScheduledPaymentStatusLabels,
  ScheduledPaymentStatusTones,
  PaymentScheduleSourceLabels,
  PayProfileConfidenceLabels,
  PayProfileConfidenceTones,
} from '../lib/enums';
import { formatDateTime, formatMoney, formatRelative, formatPercent, fromDateInputValue, toDateInputValue } from '../lib/format';
import type {
  PayablesAgentSetupDto,
  ScheduledPaymentDto,
  UpdatePayablesAgentSetupRequest,
  VendorPaymentProfileDto,
  VendorStageRow,
} from '../lib/types';

const PAY_TYPE_OPTIONS = [
  { value: 'Check', label: 'Check / bank transfer' },
  { value: 'CreditCard', label: 'Credit card' },
];

const RUN_DAYS: { key: keyof UpdatePayablesAgentSetupRequest; label: string }[] = [
  { key: 'runOnMondays', label: 'Mon' },
  { key: 'runOnTuesdays', label: 'Tue' },
  { key: 'runOnWednesdays', label: 'Wed' },
  { key: 'runOnThursdays', label: 'Thu' },
  { key: 'runOnFridays', label: 'Fri' },
  { key: 'runOnSaturdays', label: 'Sat' },
  { key: 'runOnSundays', label: 'Sun' },
];

type ScheduledPaymentColumn = 'vendor' | 'bill' | 'amount' | 'scheduled' | 'status' | 'confidence';

function scheduledPaymentSortValue(item: ScheduledPaymentDto, key: ScheduledPaymentColumn): string | number {
  switch (key) {
    case 'vendor':
      return item.vendorName || item.qboVendorId;
    case 'bill':
      return item.billDocNumber ?? item.billId;
    case 'amount':
      return item.remainingAmount ?? 0;
    case 'scheduled':
      return item.scheduledDateUtc ?? '';
    case 'status':
      return item.status;
    case 'confidence':
      return item.confidence;
    default:
      return '';
  }
}

type VendorProfileColumn = 'vendor' | 'samples' | 'medianDays' | 'onTime' | 'confidence' | 'lastPaid';

function vendorProfileSortValue(p: VendorPaymentProfileDto, key: VendorProfileColumn): string | number {
  switch (key) {
    case 'vendor':
      return p.vendorName || p.qboVendorId;
    case 'samples':
      return p.sampleCount;
    case 'medianDays':
      return p.medianDaysToPay;
    case 'onTime':
      return p.onTimeRatio ?? 0;
    case 'confidence':
      return p.confidence;
    case 'lastPaid':
      return p.lastPaymentUtc ?? '';
    default:
      return '';
  }
}

type VendorStageColumn = 'vendor' | 'stage' | 'samples' | 'confidence' | 'lastAdvanced' | 'excluded' | 'overrideDays';

function vendorStageSortValue(row: VendorStageRow, key: VendorStageColumn): string | number {
  switch (key) {
    case 'vendor':
      return row.override.vendorName || row.override.qboVendorId;
    case 'stage':
      return row.override.stage;
    case 'samples':
      return row.profile?.sampleCount ?? 0;
    case 'confidence':
      return row.profile?.confidence ?? -1;
    case 'lastAdvanced':
      return row.override.stageAdvancedUtc ?? '';
    case 'excluded':
      return row.override.excludeFromAgent ? 1 : 0;
    case 'overrideDays':
      return row.override.overrideDays ?? 0;
    default:
      return '';
  }
}

function toDraft(setup: PayablesAgentSetupDto): UpdatePayablesAgentSetupRequest {
  return {
    enabled: setup.enabled,
    learningEnabled: setup.learningEnabled,
    botLearningEnabled: setup.botLearningEnabled,
    schedulingEnabled: setup.schedulingEnabled,
    executionEnabled: setup.executionEnabled,
    defaultNewVendorStage: setup.defaultNewVendorStage,
    requireUserConfirmation: setup.requireUserConfirmation,
    defaultBankAccountId: setup.defaultBankAccountId,
    defaultBankAccountName: setup.defaultBankAccountName,
    defaultPayType: setup.defaultPayType,
    minLeadDays: setup.minLeadDays,
    capAtDueDate: setup.capAtDueDate,
    followHistoryPastDue: setup.followHistoryPastDue,
    snapToRunDay: setup.snapToRunDay,
    runOnMondays: setup.runOnMondays,
    runOnTuesdays: setup.runOnTuesdays,
    runOnWednesdays: setup.runOnWednesdays,
    runOnThursdays: setup.runOnThursdays,
    runOnFridays: setup.runOnFridays,
    runOnSaturdays: setup.runOnSaturdays,
    runOnSundays: setup.runOnSundays,
    fallbackDays: setup.fallbackDays,
    rollOffWeekends: setup.rollOffWeekends,
    learningWindowMonths: setup.learningWindowMonths,
    minSamples: setup.minSamples,
    profileRefreshHours: setup.profileRefreshHours,
    autoPostMaxAmount: setup.autoPostMaxAmount,
  };
}

function PayablesAgentContent(): JSX.Element {
  const toast = useToast();
  const state = useAsync((signal) => payablesAgent.getSetup(signal), []);
  const [draft, setDraft] = useState<UpdatePayablesAgentSetupRequest | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (state.data && !dirty) setDraft(toDraft(state.data));
  }, [state.data, dirty]);

  const set = <K extends keyof UpdatePayablesAgentSetupRequest>(key: K, value: UpdatePayablesAgentSetupRequest[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
    setDirty(true);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await payablesAgent.updateSetup(draft);
      state.setData(updated);
      setDirty(false);
      toast.success('Payables Agent settings saved');
    } catch (error) {
      const message = errorMessage(error);
      setSaveError(message);
      toast.error('Save failed', message);
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setBusy('run-now');
    try {
      const outcome = await payablesAgent.runNow();
      if (outcome.success) {
        toast.success(
          'Payables Agent ran',
          `${outcome.samplesInserted} sample(s) captured, ${outcome.newlyScheduled} newly scheduled, `
            + `${outcome.executed} paid, ${outcome.readyForPayment} ready, ${outcome.sentForApproval} sent for approval, ${outcome.held} held.`,
        );
      } else {
        toast.error('Could not run the Payables Agent', outcome.message ?? 'Refused.');
      }
      state.reload();
    } catch (error) {
      toast.error('Could not run the Payables Agent', errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  if (state.loading && !state.data) {
    return (
      <>
        <PageHeader title="Payables Agent" />
        <CardSkeleton />
      </>
    );
  }

  if (state.error && !state.data) {
    return (
      <>
        <PageHeader title="Payables Agent" />
        <ErrorState message="Payables Agent settings could not be loaded." detail={state.error} onRetry={state.reload} />
      </>
    );
  }

  if (!draft || !state.data) return <EmptyState title="No Payables Agent settings returned" />;

  return (
    <>
      <PageHeader
        title="Payables Agent"
        description="Learns each vendor's payment timing from QuickBooks history and schedules — or, when you allow it, pays — bills on their own."
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setDraft(toDraft(state.data!));
                setDirty(false);
              }}
              disabled={!dirty || saving}
            >
              Discard changes
            </button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </>
        }
      />

      <InlineError message={saveError} />

      {draft.enabled && draft.executionEnabled && draft.defaultNewVendorStage === VendorAgentStage.AutoPost ? (
        <div className="banner banner-warn" role="alert">
          <div className="banner-body">
            <p className="banner-title">New vendors start at Auto-post</p>
            <p className="banner-detail">
              Every vendor still has to be advanced one rung at a time below, but once a vendor reaches Auto-post,
              bills at or below {formatMoney(draft.autoPostMaxAmount || 0)} pay themselves in QuickBooks unattended
              the moment they come due. Everything above that amount is left Ready for Payment for a person.
            </p>
          </div>
        </div>
      ) : null}

      <div className="grid-2">
        <Panel title="Master switches">
          <CheckboxField label="Enabled" checked={draft.enabled} onChange={(v) => set('enabled', v)}
            hint="Off means nothing is learned, scheduled or paid for this company." />
          <CheckboxField label="Learning enabled" checked={draft.learningEnabled} onChange={(v) => set('learningEnabled', v)}
            hint="Capture payment-behaviour samples from QuickBooks bill payments and compute vendor profiles." />
          <CheckboxField label="Share payment behaviour with iERP AI" checked={draft.botLearningEnabled}
            onChange={(v) => set('botLearningEnabled', v)}
            hint="Sends how this company pays its vendors to iERP AI and uses the payment profiles it learns in return. Off keeps every vendor on payment history held here." />
          <CheckboxField label="Scheduling enabled" checked={draft.schedulingEnabled} onChange={(v) => set('schedulingEnabled', v)}
            hint="Create a Scheduled Payment with a suggested date when a bill comes in open." />
          <CheckboxField label="Execution enabled" checked={draft.executionEnabled} onChange={(v) => set('executionEnabled', v)}
            hint="Let the background pass act on Scheduled Payments that are due, per the mode below." />
          <CheckboxField label="Require confirmation before scheduling" checked={draft.requireUserConfirmation}
            onChange={(v) => set('requireUserConfirmation', v)}
            hint="New items start Proposed and need a click before the agent may act on them." />
        </Panel>

        <Panel title="New vendor default" bodyClassName="form-grid">
          <SelectField
            label="Stage a vendor starts at"
            value={String(draft.defaultNewVendorStage)}
            onChange={(v) => set('defaultNewVendorStage', Number(v) as VendorAgentStage)}
            options={enumOptions(VendorAgentStageLabels)}
            hint={VendorAgentStageDescriptions[draft.defaultNewVendorStage]}
          />
          <p className="field-hint form-span-2">
            This only sets where a brand-new vendor begins. Every vendor still climbs the ladder one rung at a time,
            reviewed and approved below — this default never skips a vendor ahead.
          </p>
          <TextField
            label="Auto-post maximum amount"
            type="number"
            value={String(draft.autoPostMaxAmount)}
            onChange={(v) => set('autoPostMaxAmount', Number(v) || 0)}
            hint="Applies to any vendor once it reaches the Auto-post stage. 0 means no maximum — every due payment posts unattended. Above this amount, the item is left Ready for Payment instead."
          />
          <TextField
            label="Default bank / payment account id"
            value={draft.defaultBankAccountId ?? ''}
            onChange={(v) => set('defaultBankAccountId', v || null)}
            hint="The QuickBooks account id payments are made from. Find it on the Accounts page."
          />
          <TextField
            label="Account name (for display only)"
            value={draft.defaultBankAccountName ?? ''}
            onChange={(v) => set('defaultBankAccountName', v || null)}
          />
          <SelectField
            label="Default pay type"
            value={draft.defaultPayType}
            onChange={(v) => set('defaultPayType', v)}
            options={PAY_TYPE_OPTIONS}
          />
        </Panel>
      </div>

      <Panel title="Scheduling policy" bodyClassName="form-grid">
        <TextField label="Minimum lead days" type="number" value={String(draft.minLeadDays)}
          onChange={(v) => set('minLeadDays', Number(v) || 0)}
          hint="A payment is never scheduled earlier than this many days after the bill's posting date." />
        <TextField label="Fallback days" type="number" value={String(draft.fallbackDays)}
          onChange={(v) => set('fallbackDays', Number(v) || 0)}
          hint="Used when a vendor has neither payment history nor a due date." />
        <CheckboxField label="Cap at due date" checked={draft.capAtDueDate} onChange={(v) => set('capAtDueDate', v)}
          hint="A suggested date later than the due date is pulled back to it." />
        <CheckboxField label="Follow history past due" checked={draft.followHistoryPastDue} onChange={(v) => set('followHistoryPastDue', v)}
          hint="Allow the learned payment period to schedule after the due date. Off by default." />
        <CheckboxField label="Roll off weekends" checked={draft.rollOffWeekends} onChange={(v) => set('rollOffWeekends', v)}
          hint="A suggested date on a Saturday or Sunday moves to the nearest weekday." />
        <CheckboxField label="Snap to run day" checked={draft.snapToRunDay} onChange={(v) => set('snapToRunDay', v)}
          hint="Move to the next configured run day below, or the vendor's usual payment weekday when none are set." />
        {draft.snapToRunDay ? (
          <div className="form-span-2">
            <p className="field-label">Run days</p>
            <div className="checkbox-row">
              {RUN_DAYS.map(({ key, label }) => (
                <CheckboxField
                  key={key}
                  label={label}
                  checked={Boolean(draft[key])}
                  onChange={(v) => set(key, v as never)}
                />
              ))}
            </div>
          </div>
        ) : null}
      </Panel>

      <Panel title="Learning" bodyClassName="form-grid">
        <TextField label="Learning window (months)" type="number" value={String(draft.learningWindowMonths)}
          onChange={(v) => set('learningWindowMonths', Math.max(1, Number(v) || 1))} />
        <TextField label="Minimum samples" type="number" value={String(draft.minSamples)}
          onChange={(v) => set('minSamples', Math.max(1, Number(v) || 1))}
          hint="Paid bills needed before a vendor's profile is trusted over payment terms." />
        <TextField label="Profile refresh (hours)" type="number" value={String(draft.profileRefreshHours)}
          onChange={(v) => set('profileRefreshHours', Math.max(1, Number(v) || 1))} />
      </Panel>

      <Panel title="Background pass">
        <KeyValueList columns={1}>
          <div className="kv">
            <dt className="kv-label">Last run</dt>
            <dd className="kv-value">
              {state.data.lastRunCompletedUtc ? formatRelative(state.data.lastRunCompletedUtc) : 'never'}
              {state.data.lastRunMessage ? <span className="cell-sub">{state.data.lastRunMessage}</span> : null}
            </dd>
          </div>
          <div className="kv">
            <dt className="kv-label">Profiles computed</dt>
            <dd className="kv-value">{state.data.profilesComputedUtc ? formatRelative(state.data.profilesComputedUtc) : 'never'}</dd>
          </div>
        </KeyValueList>
        {state.data.lastErrorMessage ? <InlineError message={state.data.lastErrorMessage} /> : null}
        <Toolbar>
          <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={runNow}>
            {busy === 'run-now' ? 'Running…' : 'Run now'}
          </button>
        </Toolbar>
        <p className="field-hint">Runs automatically in the background on its own schedule; this is a manual trigger for the same pass.</p>
      </Panel>

      <ScheduledPaymentsPanel />
      <VendorProfilesPanel />
      <VendorStagesPanel />
    </>
  );
}

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All open' },
  ...enumOptions(ScheduledPaymentStatusLabels),
];

function ScheduledPaymentsPanel(): JSX.Element {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('');
  const state = useAsync(
    (signal) => payablesAgent.listScheduledPayments(statusFilter === '' ? null : (Number(statusFilter) as ScheduledPaymentStatus), signal),
    [statusFilter],
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rescheduleId, setRescheduleId] = useState<string | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const table = useSortableTable<ScheduledPaymentColumn>({
    storageKey: 'payablesScheduledPaymentsColumnWidths',
    columns: {
      vendor: { minWidth: 140 },
      bill: { minWidth: 140 },
      amount: { minWidth: 100 },
      scheduled: { minWidth: 140 },
      status: { minWidth: 130 },
      confidence: { minWidth: 110 },
    },
  });

  const run = async (item: ScheduledPaymentDto, name: string, action: () => Promise<ScheduledPaymentDto>) => {
    setBusyId(item.id);
    try {
      await action();
      toast.success(name);
      state.reload();
    } catch (error) {
      toast.error(`${name} failed`, errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const startReschedule = (item: ScheduledPaymentDto) => {
    setRescheduleId(item.id);
    setRescheduleDate(toDateInputValue(item.scheduledDateUtc));
  };

  const submitReschedule = async (item: ScheduledPaymentDto) => {
    const value = fromDateInputValue(rescheduleDate);
    if (!value) return;
    await run(item, 'Rescheduled', () => payablesAgent.reschedule(item.id, value));
    setRescheduleId(null);
  };

  return (
    <Panel
      title="Scheduled payments"
      description="Everything the agent has scheduled, is holding, or has paid."
      actions={
        <SelectField label="" value={statusFilter} onChange={setStatusFilter} options={STATUS_FILTER_OPTIONS} className="filter-select" />
      }
    >
      {state.error ? <InlineError message={state.error} /> : null}
      <TableWrap busy={state.loading} tableClassName="data-table-sortable">
        <colgroup>
          <col style={table.columnStyle('vendor')} />
          <col style={table.columnStyle('bill')} />
          <col style={table.columnStyle('amount')} />
          <col style={table.columnStyle('scheduled')} />
          <col style={table.columnStyle('status')} />
          <col style={table.columnStyle('confidence')} />
          <col />
        </colgroup>
        <thead>
          <tr>
            <SortableTh
              label="Vendor"
              sortKey="vendor"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('vendor')}
              onResizeStart={table.beginResize('vendor')}
            />
            <SortableTh
              label="Bill"
              sortKey="bill"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('bill')}
              onResizeStart={table.beginResize('bill')}
            />
            <SortableTh
              label="Amount"
              sortKey="amount"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('amount')}
              onResizeStart={table.beginResize('amount')}
            />
            <SortableTh
              label="Scheduled"
              sortKey="scheduled"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('scheduled')}
              onResizeStart={table.beginResize('scheduled')}
            />
            <SortableTh
              label="Status"
              sortKey="status"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('status')}
              onResizeStart={table.beginResize('status')}
            />
            <SortableTh
              label="Confidence"
              sortKey="confidence"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('confidence')}
              onResizeStart={table.beginResize('confidence')}
            />
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {table.sort(state.data ?? [], scheduledPaymentSortValue).map((item) => (
            <tr key={item.id}>
              <td>{item.vendorName || item.qboVendorId}</td>
              <td>
                {item.billDocNumber ?? item.billId}
                {item.holdReason ? <span className="cell-sub">{item.holdReason}</span> : null}
                {item.paymentOutcomeMessage
                  ? <span className="cell-sub">{item.paymentOutcomeMessage}</span>
                  : null}
              </td>
              <td>{formatMoney(item.remainingAmount, item.currencyCode)}</td>
              <td>
                {rescheduleId === item.id ? (
                  <span className="inline-edit">
                    <input
                      type="date"
                      className="input input-sm"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                    />
                    <button type="button" className="btn btn-link" onClick={() => submitReschedule(item)}>Save</button>
                    <button type="button" className="btn btn-link" onClick={() => setRescheduleId(null)}>Cancel</button>
                  </span>
                ) : (
                  <>
                    {formatDateTime(item.scheduledDateUtc)}
                    <span className="cell-sub">{PaymentScheduleSourceLabels[item.scheduleSource]}</span>
                  </>
                )}
              </td>
              <td>
                <Chip tone={ScheduledPaymentStatusTones[item.status]}>{ScheduledPaymentStatusLabels[item.status]}</Chip>
              </td>
              <td>
                <Chip tone={PayProfileConfidenceTones[item.confidence]}>{PayProfileConfidenceLabels[item.confidence]}</Chip>
              </td>
              <td className="col-actions">
                {[ScheduledPaymentStatus.Proposed, ScheduledPaymentStatus.Scheduled, ScheduledPaymentStatus.OnHold].includes(item.status) ? (
                  <button type="button" className="btn btn-link" disabled={busyId !== null} onClick={() => startReschedule(item)}>Reschedule</button>
                ) : null}
                {item.status === ScheduledPaymentStatus.Proposed ? (
                  <button type="button" className="btn btn-link" disabled={busyId !== null}
                    onClick={() => run(item, 'Confirmed', () => payablesAgent.confirm(item.id))}>Confirm</button>
                ) : null}
                {[ScheduledPaymentStatus.Proposed, ScheduledPaymentStatus.Scheduled].includes(item.status) ? (
                  <button type="button" className="btn btn-link" disabled={busyId !== null}
                    onClick={() => run(item, 'Held', () => payablesAgent.hold(item.id, null))}>Hold</button>
                ) : null}
                {item.status === ScheduledPaymentStatus.OnHold ? (
                  <button type="button" className="btn btn-link" disabled={busyId !== null}
                    onClick={() => run(item, 'Released', () => payablesAgent.release(item.id))}>Release</button>
                ) : null}
                {[ScheduledPaymentStatus.Proposed, ScheduledPaymentStatus.Scheduled, ScheduledPaymentStatus.OnHold, ScheduledPaymentStatus.ReadyForPayment].includes(item.status) ? (
                  <button type="button" className="btn btn-link btn-danger" disabled={busyId !== null}
                    onClick={() => window.confirm(`Pay ${item.vendorName ?? item.qboVendorId} ${formatMoney(item.remainingAmount, item.currencyCode)} now? This creates a real QuickBooks payment.`)
                      && run(item, 'Paid', () => payablesAgent.payNow(item.id))}>Pay now</button>
                ) : null}
                {item.status === ScheduledPaymentStatus.PaymentOutcomeUnknown
                  || item.status === ScheduledPaymentStatus.Paying ? (
                  <button type="button" className="btn btn-link" disabled={busyId !== null}
                    onClick={() => run(item, 'Checked', () =>
                      payablesAgent.reconcile(item.id).then((r) => r.item))}>
                    Check QuickBooks
                  </button>
                ) : null}
                {item.status === ScheduledPaymentStatus.PendingApproval ? (
                  <>
                    <button type="button" className="btn btn-link" disabled={busyId !== null}
                      onClick={() => run(item, 'Approved', () => payablesAgent.approve(item.id))}>Approve</button>
                    <button type="button" className="btn btn-link" disabled={busyId !== null}
                      onClick={() => run(item, 'Rejected', () => payablesAgent.reject(item.id, null))}>Reject</button>
                  </>
                ) : null}
                {[ScheduledPaymentStatus.Proposed, ScheduledPaymentStatus.Scheduled, ScheduledPaymentStatus.OnHold].includes(item.status) ? (
                  <button type="button" className="btn btn-link btn-danger" disabled={busyId !== null}
                    onClick={() => run(item, 'Cancelled', () => payablesAgent.cancel(item.id, null))}>Cancel</button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!state.loading && (state.data?.length ?? 0) === 0 ? (
        <p className="field-hint">Nothing here yet. Items appear once the agent (or Run now above) has scheduled an open bill.</p>
      ) : null}
    </Panel>
  );
}

function VendorProfilesPanel(): JSX.Element {
  const state = useAsync((signal) => payablesAgent.listVendorProfiles(signal), []);
  const table = useSortableTable<VendorProfileColumn>({
    storageKey: 'payablesVendorProfilesColumnWidths',
    columns: {
      vendor: { minWidth: 140 },
      samples: { minWidth: 90 },
      medianDays: { minWidth: 140 },
      onTime: { minWidth: 90 },
      confidence: { minWidth: 110 },
      lastPaid: { minWidth: 120 },
    },
  });

  return (
    <Panel title="Vendor payment profiles" description="What the agent has learned about how each vendor actually gets paid.">
      {state.error ? <InlineError message={state.error} /> : null}
      <TableWrap busy={state.loading} tableClassName="data-table-sortable">
        <colgroup>
          <col style={table.columnStyle('vendor')} />
          <col style={table.columnStyle('samples')} />
          <col style={table.columnStyle('medianDays')} />
          <col style={table.columnStyle('onTime')} />
          <col style={table.columnStyle('confidence')} />
          <col style={table.columnStyle('lastPaid')} />
        </colgroup>
        <thead>
          <tr>
            <SortableTh
              label="Vendor"
              sortKey="vendor"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('vendor')}
              onResizeStart={table.beginResize('vendor')}
            />
            <SortableTh
              label="Samples"
              sortKey="samples"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('samples')}
              onResizeStart={table.beginResize('samples')}
            />
            <SortableTh
              label="Median days to pay"
              sortKey="medianDays"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('medianDays')}
              onResizeStart={table.beginResize('medianDays')}
            />
            <SortableTh
              label="On-time"
              sortKey="onTime"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('onTime')}
              onResizeStart={table.beginResize('onTime')}
            />
            <SortableTh
              label="Confidence"
              sortKey="confidence"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('confidence')}
              onResizeStart={table.beginResize('confidence')}
            />
            <SortableTh
              label="Last payment"
              sortKey="lastPaid"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('lastPaid')}
              onResizeStart={table.beginResize('lastPaid')}
            />
          </tr>
        </thead>
        <tbody>
          {table.sort((state.data ?? []).filter((p) => p.hasProfile), vendorProfileSortValue).map((p) => (
            <tr key={p.id}>
              <td>{p.vendorName || p.qboVendorId}</td>
              <td>{p.sampleCount}</td>
              <td>{p.medianDaysToPay.toFixed(1)} days{p.biasDays !== 0 ? <span className="cell-sub">{p.biasDays > 0 ? '+' : ''}{p.biasDays} correction</span> : null}</td>
              <td>{formatPercent(p.onTimeRatio)}</td>
              <td><Chip tone={PayProfileConfidenceTones[p.confidence]}>{PayProfileConfidenceLabels[p.confidence]}</Chip></td>
              <td>{p.lastPaymentUtc ? formatRelative(p.lastPaymentUtc) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!state.loading && (state.data ?? []).filter((p) => p.hasProfile).length === 0 ? (
        <p className="field-hint">No vendor has enough paid-bill history yet — profiles appear once a vendor crosses the minimum-samples threshold above.</p>
      ) : null}
    </Panel>
  );
}

const NEXT_STAGE: Partial<Record<VendorAgentStage, VendorAgentStage>> = {
  [VendorAgentStage.Observing]: VendorAgentStage.Scheduling,
  [VendorAgentStage.Scheduling]: VendorAgentStage.ManualPay,
  [VendorAgentStage.ManualPay]: VendorAgentStage.ApprovalGated,
  [VendorAgentStage.ApprovalGated]: VendorAgentStage.AutoPost,
};

function VendorStagesPanel(): JSX.Element {
  const toast = useToast();
  const state = useAsync((signal) => payablesAgent.listVendorStages(signal), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [editingDaysId, setEditingDaysId] = useState<string | null>(null);
  const [daysValue, setDaysValue] = useState('');
  const table = useSortableTable<VendorStageColumn>({
    storageKey: 'payablesVendorStagesColumnWidths',
    columns: {
      vendor: { minWidth: 140 },
      stage: { minWidth: 150 },
      samples: { minWidth: 90 },
      confidence: { minWidth: 110 },
      lastAdvanced: { minWidth: 130 },
      excluded: { minWidth: 100 },
      overrideDays: { minWidth: 120 },
    },
  });

  const advance = async (row: VendorStageRow) => {
    const next = NEXT_STAGE[row.override.stage];
    if (!next) return;
    if (!window.confirm(
      `Move ${row.override.vendorName ?? row.override.qboVendorId} from ${VendorAgentStageLabels[row.override.stage]} `
      + `to ${VendorAgentStageLabels[next]}? ${VendorAgentStageDescriptions[next]}`,
    )) return;
    setBusy(row.override.qboVendorId);
    try {
      await payablesAgent.advanceVendorStage(row.override.qboVendorId, null);
      toast.success('Vendor advanced', `Now at ${VendorAgentStageLabels[next]}.`);
      state.reload();
    } catch (error) {
      toast.error('Could not advance vendor', errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const demote = async (row: VendorStageRow, stage: VendorAgentStage) => {
    if (!window.confirm(
      `Move ${row.override.vendorName ?? row.override.qboVendorId} back to ${VendorAgentStageLabels[stage]}?`,
    )) return;
    setBusy(row.override.qboVendorId);
    try {
      await payablesAgent.setVendorStage(row.override.qboVendorId, { stage, note: null });
      toast.success('Vendor stage changed', `Now at ${VendorAgentStageLabels[stage]}.`);
      state.reload();
    } catch (error) {
      toast.error('Could not change vendor stage', errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const toggleExclude = async (row: VendorStageRow) => {
    setBusy(row.override.qboVendorId);
    try {
      await payablesAgent.setVendorExclusion(row.override.qboVendorId, { excludeFromAgent: !row.override.excludeFromAgent });
      toast.success(row.override.excludeFromAgent ? 'Vendor re-included' : 'Vendor excluded');
      state.reload();
    } catch (error) {
      toast.error('Could not change exclusion', errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const startEditDays = (row: VendorStageRow) => {
    setEditingDaysId(row.override.qboVendorId);
    setDaysValue(String(row.override.overrideDays || ''));
  };

  const saveDays = async (row: VendorStageRow) => {
    setBusy(row.override.qboVendorId);
    try {
      await payablesAgent.setVendorOverrideDays(row.override.qboVendorId, { overrideDays: Number(daysValue) || 0 });
      toast.success('Override days saved');
      setEditingDaysId(null);
      state.reload();
    } catch (error) {
      toast.error('Could not save override days', errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel
      title="Vendor stages"
      description="Every vendor starts on the ladder set above. Advance one rung at a time once you've reviewed how the agent behaves — it never advances itself."
    >
      {state.error ? <InlineError message={state.error} /> : null}
      <TableWrap busy={state.loading} tableClassName="data-table-sortable">
        <colgroup>
          <col style={table.columnStyle('vendor')} />
          <col style={table.columnStyle('stage')} />
          <col style={table.columnStyle('samples')} />
          <col style={table.columnStyle('confidence')} />
          <col style={table.columnStyle('lastAdvanced')} />
          <col style={table.columnStyle('excluded')} />
          <col style={table.columnStyle('overrideDays')} />
          <col />
        </colgroup>
        <thead>
          <tr>
            <SortableTh
              label="Vendor"
              sortKey="vendor"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('vendor')}
              onResizeStart={table.beginResize('vendor')}
            />
            <SortableTh
              label="Stage"
              sortKey="stage"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('stage')}
              onResizeStart={table.beginResize('stage')}
            />
            <SortableTh
              label="Samples"
              sortKey="samples"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('samples')}
              onResizeStart={table.beginResize('samples')}
            />
            <SortableTh
              label="Confidence"
              sortKey="confidence"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('confidence')}
              onResizeStart={table.beginResize('confidence')}
            />
            <SortableTh
              label="Last advanced"
              sortKey="lastAdvanced"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('lastAdvanced')}
              onResizeStart={table.beginResize('lastAdvanced')}
            />
            <SortableTh
              label="Excluded"
              sortKey="excluded"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('excluded')}
              onResizeStart={table.beginResize('excluded')}
            />
            <SortableTh
              label="Override days"
              sortKey="overrideDays"
              currentSortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              style={table.columnStyle('overrideDays')}
              onResizeStart={table.beginResize('overrideDays')}
            />
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {table.sort(state.data ?? [], vendorStageSortValue).map((row) => {
            const { override, profile } = row;
            const next = NEXT_STAGE[override.stage];
            const rowBusy = busy === override.qboVendorId;
            return (
              <tr key={override.id}>
                <td>{override.vendorName || override.qboVendorId}</td>
                <td>
                  <Chip tone={VendorAgentStageTones[override.stage]}>{VendorAgentStageLabels[override.stage]}</Chip>
                  <span className="cell-sub">{VendorAgentStageDescriptions[override.stage]}</span>
                </td>
                <td>{profile?.sampleCount ?? 0}</td>
                <td>{profile ? <Chip tone={PayProfileConfidenceTones[profile.confidence]}>{PayProfileConfidenceLabels[profile.confidence]}</Chip> : '—'}</td>
                <td>
                  {override.stageAdvancedUtc ? (
                    <>
                      {formatRelative(override.stageAdvancedUtc)}
                      {override.stageAdvancedByEmail ? <span className="cell-sub">{override.stageAdvancedByEmail}</span> : null}
                      {override.stageNote ? <span className="cell-sub">{override.stageNote}</span> : null}
                    </>
                  ) : '—'}
                </td>
                <td>
                  <button type="button" className="btn btn-link" disabled={rowBusy} onClick={() => toggleExclude(row)}>
                    {override.excludeFromAgent ? <Chip tone="bad">Excluded</Chip> : <Chip tone="neutral">No</Chip>}
                  </button>
                </td>
                <td>
                  {editingDaysId === override.qboVendorId ? (
                    <span className="inline-edit">
                      <input
                        type="number"
                        className="input input-sm"
                        value={daysValue}
                        onChange={(e) => setDaysValue(e.target.value)}
                      />
                      <button type="button" className="btn btn-link" onClick={() => saveDays(row)}>Save</button>
                      <button type="button" className="btn btn-link" onClick={() => setEditingDaysId(null)}>Cancel</button>
                    </span>
                  ) : (
                    <button type="button" className="btn btn-link" disabled={rowBusy} onClick={() => startEditDays(row)}>
                      {override.overrideDays > 0 ? `${override.overrideDays} days` : 'set…'}
                    </button>
                  )}
                </td>
                <td className="col-actions">
                  {next && !override.excludeFromAgent ? (
                    <button type="button" className="btn btn-link" disabled={rowBusy} onClick={() => advance(row)}>
                      {rowBusy ? 'Working…' : `Advance to ${VendorAgentStageLabels[next]}`}
                    </button>
                  ) : null}
                  {override.stage !== VendorAgentStage.Observing ? (
                    <button type="button" className="btn btn-link btn-danger" disabled={rowBusy}
                      onClick={() => demote(row, VendorAgentStage.Observing)}>Reset to Observing</button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </TableWrap>
      {!state.loading && (state.data?.length ?? 0) === 0 ? (
        <p className="field-hint">No vendor has been seen yet — a vendor appears here the first time the agent observes an open bill for it.</p>
      ) : null}
    </Panel>
  );
}

/** Admin-only screen; the guard is repeated here so it holds however the page is rendered. */
export function PayablesAgentPage(): JSX.Element {
  return (
    <RequireRole minRole={GatewayRole.Admin}>
      <PayablesAgentContent />
    </RequireRole>
  );
}
